/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, MessageBarButton } from '@fluentui/react/lib/Button';
import { IMessageBarProps, IMessageBarStyles, MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { IStackProps, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { routeTable } from 'src/core/utils/routeTable';
import { getLanguageStudioBaseUrl } from 'src/core/utils/routingUtils';

const messages = defineMessages({
    announcement: {
        id: 'portal.cluMigrationBanner.announcement',
        defaultMessage: 'Announcement:'
    },
    description1: {
        id: 'portal.cluMigrationBanner.description1',
        defaultMessage:
            'Language Understanding (LUIS) will be retired on 1 October 2025. Starting 1 April 2023, users will not be able to create new LUIS resources.'
    },
    description2a: {
        id: 'portal.cluMigrationBanner.description2a',
        defaultMessage: 'If you are a new user, get started with conversational language understanding (CLU), the next generation of LUIS.'
    },
    description2b: {
        id: 'portal.cluMigrationBanner.description2b',
        defaultMessage: 'about conversational language understanding (CLU).'
    },
    learnMore: {
        id: 'portal.cluMigrationBanner.learnMore',
        defaultMessage: 'Learn more'
    },
    description3a: {
        id: 'portal.cluMigrationBanner.description3a',
        defaultMessage: 'If you are an existing user,'
    },
    description3b: {
        id: 'portal.cluMigrationBanner.description3b',
        defaultMessage:
            'your LUIS applications to conversational language understanding (CLU) to take advantage of multilingual capabilities and state-of-the-art machine learning models.'
    },
    description3c: {
        id: 'portal.cluMigrationBanner.description3c',
        defaultMessage: 'about migrating to conversational language understanding (CLU).'
    },
    migrate: {
        id: 'portal.cluMigrationBanner.migrate',
        defaultMessage: 'migrate'
    },
    migrateToClu: {
        id: 'portal.cluMigrationBanner.migrateToClu',
        defaultMessage: 'Migrate to CLU'
    },
    getStartedWithClu: {
        id: 'portal.cluMigrationBanner.getStartedWithClu',
        defaultMessage: 'Get started with CLU'
    }
});

const messageBarStyles = classNamesFunction<IMessageBarProps, IMessageBarStyles>()({
    text: { height: 130 },
    root: { minHeight: 140 },
    content: { alignItems: 'center' },
    iconContainer: { display: 'none' },
    innerText: { width: '100%', overflowY: 'auto' }
});

const messageStyles = classNamesFunction<IStackProps, IStackStyles>()({ root: { margin: '10px 15px 5px' } });

const buttonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { minWidth: '110px' } });

export const CluMigrationBanner = () => {
    const { localize } = useLocalization();
    const { dispatcher } = useGlobalState();

    const renderDescription1 = () => (
        <LuisText>
            <ExternalLink href="https://aka.ms/luis-retirement">{localize(messages.announcement)}</ExternalLink>
            &nbsp;
            <LuisText>{localize(messages.description1)}</LuisText>
        </LuisText>
    );

    const renderDescription2 = () => (
        <LuisText>
            <LuisText>{localize(messages.description2a)}</LuisText>
            &nbsp;
            <ExternalLink href="https://aka.ms/clu-docs">{localize(messages.learnMore)}</ExternalLink>
            &nbsp;
            <LuisText>{localize(messages.description2b)}</LuisText>
        </LuisText>
    );

    const renderDescription3 = () => (
        <LuisText>
            <LuisText>{localize(messages.description3a)}</LuisText>
            &nbsp;
            <ExternalLink onClick={() => dispatcher.dispatch('updateCluMigrationModalVisibility', { isVisible: true })}>
                {localize(messages.migrate)}
            </ExternalLink>
            &nbsp;
            <LuisText>{localize(messages.description3b)}</LuisText>
            &nbsp;
            <ExternalLink href="https://aka.ms/luis-migration-clu">{localize(messages.learnMore)}</ExternalLink>
            &nbsp;
            <LuisText>{localize(messages.description3c)}</LuisText>
        </LuisText>
    );

    return (
        <MessageBar messageBarType={MessageBarType.warning} styles={messageBarStyles}>
            <Stack tokens={{ childrenGap: 10 }} styles={messageStyles}>
                <ol>
                    <Stack tokens={{ childrenGap: 5 }}>
                        <li>{renderDescription1()}</li>
                        <li>{renderDescription2()}</li>
                        <li>{renderDescription3()}</li>
                    </Stack>
                </ol>

                <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 5 }}>
                    <MessageBarButton
                        styles={buttonStyles}
                        onClick={() => dispatcher.dispatch('updateCluMigrationModalVisibility', { isVisible: true })}>
                        <TruncatedText>{localize(messages.migrateToClu)}</TruncatedText>
                    </MessageBarButton>
                    <MessageBarButton
                        styles={buttonStyles}
                        onClick={() => window.open(getLanguageStudioBaseUrl().concat(routeTable.clu.projectsPage()))}>
                        <TruncatedText>{localize(messages.getStartedWithClu)}</TruncatedText>
                    </MessageBarButton>
                </Stack>
            </Stack>
        </MessageBar>
    );
};
